import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getOts, getOt, getOtConfig, getGruposPlanta, accionOt, downloadOtAdjunto } from '../api/client'
import PageHeader from '../components/PageHeader'
import { EstadoOt, OrigenOt } from '../components/ot/Etiquetas'
import { diasDesde, tonoOrigen, miembrosDe, responsableTrasGrupo } from '../components/ot/otLogica'
import { hoyLocal } from '../components/partes/fechas'
import AdmisionLote from '../components/ot/AdmisionLote'
import { agruparPorLote } from '../components/ot/loteLogica'
import { formatFecha } from '../components/unidades/tipos'

const PRIORIDADES = ['critica', 'alta', 'media', 'baja']

// Operaciones > Planta > Bandeja de admisión: requests and restocking
// proposals waiting for whoever holds "admitir" / "aprobar_reposicion"
// (named by function, not by who holds it today). Review the data and the
// documents, plan it (priority, committed date, work group, responsable;
// hours optional) and approve -- which queues the SAP tasks -- or return it
// with a reason. ?ot=<id> opens one.
export default function PlantaAdmision() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [sp, setSp] = useSearchParams()
  const elegida = sp.get('ot') ? Number(sp.get('ot')) : null
  const loteElegido = sp.get('lote') ? Number(sp.get('lote')) : null
  const [config, setConfig] = useState(null)
  const [lista, setLista] = useState(null)
  const [error, setError] = useState('')

  const cargar = useCallback(() => {
    getOts().then(os => setLista(os.filter(o => ['solicitada', 'propuesta'].includes(o.estado))
      .sort((a, b) => (a.estado_desde < b.estado_desde ? -1 : 1))))
      .catch(() => setError(t('ot.cargaFallo')))
  }, [t])
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => { getOtConfig().then(setConfig).catch(() => {}) }, [])

  const puede = config && (config.funciones.includes('admitir') || config.funciones.includes('aprobar_reposicion'))
  const elegir = (id) => setSp(id ? { ot: String(id) } : {})
  const elegirLote = (id) => setSp({ lote: String(id) })
  // A batch handled: reload the tray (some of its OTs may still wait).
  const loteHecho = () => { setSp({}); cargar() }
  const hecho = (o) => {
    // Next in the tray, if any.
    const resto = (lista || []).filter(x => x.id !== o.id)
    setLista(resto)
    elegir(resto[0]?.id)
  }

  if (config && !puede) {
    return <div className="p-8 text-sm text-gray-500">{t('ot.admision.sinFuncion')}</div>
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('ot.admision.titulo')} subtitle={t('ot.admision.subtitulo')} />
      </div>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <div className="grid gap-5 lg:grid-cols-[minmax(280px,340px)_1fr] items-start">
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-1">
            {t('ot.admision.pendientes', { n: lista?.length ?? 0 })}
          </h3>
          {!lista ? <p className="text-sm text-gray-400 px-1">{t('common:loading', { defaultValue: 'Cargando…' })}</p>
            : !lista.length ? <p className="text-sm text-gray-400 px-1 py-6">{t('ot.admision.vacia')}</p>
              : agruparPorLote(lista).map(e => e.tipo === 'lote' ? (
                <button key={`l${e.lote.id}`} type="button" onClick={() => elegirLote(e.lote.id)}
                  className={`w-full text-left rounded-xl border border-l-4 border-l-indigo-400 bg-white px-3.5 py-2.5 transition-all hover:shadow-md ${
                    loteElegido === e.lote.id ? 'border-gray-900 ring-1 ring-gray-900 shadow-md' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">{e.lote.codigo}</span>
                    <span className="text-xs text-gray-500">{t('ot.lote.nOt', { count: e.ots.length })}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1 line-clamp-2">{e.ots[0].descripcion || e.ots.map(o => o.articulo_codigo).slice(0, 3).join(', ')}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {e.ots[0].proyecto ? (e.ots[0].proyecto.code || e.ots[0].proyecto.name) : t(`ot.origenes.${e.ots[0].origen}`)} · {e.ots[0].solicitante?.display_name}
                    {' · '}{t('ot.admision.esperando', { dias: t('ot.dias', { count: diasDesde(e.ots[0].estado_desde) }) })}
                  </p>
                </button>
              ) : (o => (
                <button key={o.id} type="button" onClick={() => elegir(o.id)}
                  className={`w-full text-left rounded-xl border border-l-4 px-3.5 py-2.5 transition-all hover:shadow-md ${tonoOrigen(o.origen)} ${
                    elegida === o.id ? 'border-gray-900 ring-1 ring-gray-900 shadow-md' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold text-gray-900">{o.codigo}</span>
                    <span className="flex items-center gap-1"><OrigenOt origen={o.origen} /><EstadoOt estado={o.estado} /></span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1 line-clamp-2">{o.articulo_descripcion || o.articulo_codigo}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {o.proyecto ? (o.proyecto.code || o.proyecto.name) : t(`ot.origenes.${o.origen}`)} · {o.solicitante?.display_name}
                    {' · '}{t('ot.admision.esperando', { dias: t('ot.dias', { count: diasDesde(o.estado_desde) }) })}
                  </p>
                </button>
              ))(e.ot))}
        </section>
        <section className="min-w-0">
          {loteElegido
            ? <AdmisionLote key={`l${loteElegido}`} loteId={loteElegido} t={t} onHecho={loteHecho} onVer={id => navigate(`/operations/planta/ot?ot=${id}`)} />
            : elegida
            ? <Revision key={elegida} otId={elegida} t={t} onHecho={hecho} onVer={id => navigate(`/operations/planta/ot?ot=${id}`)} />
            : <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-400">{t('ot.admision.elige')}</div>}
        </section>
      </div>
    </div>
  )
}

function Revision({ otId, t, onHecho, onVer }) {
  const [ot, setOt] = useState(null)
  const [grupos, setGrupos] = useState([])
  const [plan, setPlan] = useState(null)
  const [devolviendo, setDevolviendo] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [faltan, setFaltan] = useState([])
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  useEffect(() => {
    Promise.all([getOt(otId), getGruposPlanta()]).then(([o, gs]) => {
      setOt(o); setGrupos(gs)
      const g = gs.find(x => x.id === (o.grupo?.id ?? o.grupo_propuesto?.id))
      const hoy = hoyLocal()
      setPlan({
        prioridad: o.prioridad || '',
        fecha_compromiso: o.fecha_compromiso || (o.fecha_requerida && o.fecha_requerida >= hoy ? o.fecha_requerida : ''),
        grupo_id: g ? String(g.id) : '',
        responsable_id: g ? responsableTrasGrupo(gs, g.id, o.responsable?.id) : '',
        horas_estimadas: o.horas_estimadas ?? '',
      })
    }).catch(() => setError(t('ot.panel.noEncontrada')))
  }, [otId, t])

  if (error && !ot) return <p className="text-sm text-red-600">{error}</p>
  if (!ot || !plan) return <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p>

  const acciones = ot.acciones.map(a => a.accion)
  const set = (k, v) => { setPlan(p => ({ ...p, [k]: v })); setFaltan(fs => fs.filter(f => f !== k && !(k === 'responsable_id' && f === 'responsable') && !(k === 'grupo_id' && f === 'grupo'))) }
  const elegirGrupo = (id) => {
    // The responsable is one of the group's people: kept if in it, else its supervisor.
    setPlan(p => ({ ...p, grupo_id: id, responsable_id: responsableTrasGrupo(grupos, id, p.responsable_id) }))
    setFaltan(fs => fs.filter(f => f !== 'grupo'))
  }
  const mal = (k) => faltan.includes(k)

  const aprobar = async () => {
    const fs = []
    if (!plan.prioridad) fs.push('prioridad')
    if (!plan.fecha_compromiso) fs.push('fecha_compromiso')
    else if (plan.fecha_compromiso < hoyLocal()) fs.push('fecha_compromiso_pasada')
    if (!plan.grupo_id) fs.push('grupo')
    if (!plan.responsable_id) fs.push('responsable')
    if (fs.length) { setFaltan(fs); setError(t('ot.faltanDatos', { lista: fs.map(f => t(`ot.faltan.${f}`)).join(', ') })); return }
    setOcupado(true); setError('')
    try {
      const o = await accionOt(ot.id, 'aprobar_y_planificar', {
        prioridad: plan.prioridad, fecha_compromiso: plan.fecha_compromiso, grupo_id: Number(plan.grupo_id),
        responsable_id: Number(plan.responsable_id),
        ...(plan.horas_estimadas !== '' ? { horas_estimadas: Number(plan.horas_estimadas) } : {}),
      })
      onHecho(o)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'validacion') setFaltan(d.faltan)
      setError(d?.code === 'validacion'
        ? t('ot.faltanDatos', { lista: d.faltan.map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })
        : d?.message || t('ot.panel.fallo'))
    }
    setOcupado(false)
  }

  const devolver = async () => {
    if (!motivo.trim()) { setError(t('ot.admision.motivoRequerido')); return }
    setOcupado(true); setError('')
    try { onHecho(await accionOt(ot.id, 'devolver', { motivo: motivo.trim() })) }
    catch (err) { setError(err.response?.data?.detail?.message || t('ot.panel.fallo')) }
    setOcupado(false)
  }

  const sinReceta = ot.tiene_receta_sap === false
  const lbl = 'block text-xs font-medium text-gray-700 mb-1'
  const inp = (k) => `input ${mal(k) ? '!border-rose-400' : ''}`

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => onVer(ot.id)} className="font-mono text-sm font-semibold text-gray-500 hover:underline">{ot.codigo}</button>
              <EstadoOt estado={ot.estado} /><OrigenOt origen={ot.origen} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mt-1">{ot.articulo_descripcion || ot.articulo_codigo}</h2>
            {ot.articulo_codigo && ot.articulo_descripcion && <p className="text-xs font-mono text-gray-500">{ot.articulo_codigo}</p>}
          </div>
          <span className="text-xs text-gray-500 shrink-0">{t('ot.admision.pidio', { quien: ot.solicitante?.display_name })}</span>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mt-4">
          <Dato label={t('ot.campos.proyecto')}>{ot.proyecto ? ot.proyecto.name : '—'}</Dato>
          <Dato label={t('ot.campos.cantidad')}>{ot.cantidad}</Dato>
          <Dato label={t('ot.campos.fecha_requerida')}>{ot.fecha_requerida ? formatFecha(ot.fecha_requerida) : '—'}</Dato>
          <Dato label={t('ot.admision.receta')}>
            {ot.tiene_receta_sap ? t('ot.si') : <span className="text-amber-700 font-medium">{t('ot.admision.sinReceta')}</span>}
          </Dato>
          {ot.descripcion && <Dato label={t('ot.campos.descripcion')} ancho>{ot.descripcion}</Dato>}
        </dl>
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-700 mb-1">{t('ot.panel.adjuntos')}</p>
          {!ot.adjuntos.length ? <p className="text-xs text-gray-400">{t('ot.panel.sinAdjuntos')}</p> : (
            <div className="flex flex-wrap gap-2">
              {ot.adjuntos.map(a => (
                <button key={a.id} type="button" onClick={() => downloadOtAdjunto(ot.id, a).catch(() => setError(t('ot.panel.fallo')))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50">
                  <span className="font-semibold uppercase text-gray-500">{t(`ot.tiposAdjunto.${a.tipo}`)}</span>
                  <span className="text-gray-800 truncate max-w-[200px]">{a.filename}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('ot.admision.planificacion')}</h3>
        <div className="space-y-3">
          <div>
            <span className={lbl}>{t('ot.campos.prioridad')} *</span>
            <div className={`inline-flex rounded-lg border p-0.5 ${mal('prioridad') ? 'border-rose-400' : 'border-gray-200'}`}>
              {PRIORIDADES.map(p => (
                <button key={p} type="button" onClick={() => set('prioridad', p)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${plan.prioridad === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {t(`ot.prioridades.${p}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t('ot.campos.fecha_compromiso')} *</label>
              <input type="date" min={hoyLocal()} className={inp(plan.fecha_compromiso && plan.fecha_compromiso < hoyLocal() ? 'fecha_compromiso_pasada' : 'fecha_compromiso')}
                value={plan.fecha_compromiso} onChange={e => { set('fecha_compromiso', e.target.value); setFaltan(fs => fs.filter(f => f !== 'fecha_compromiso_pasada')) }} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.horas_estimadas')}</label>
              <input type="number" min="0" step="0.5" className="input" value={plan.horas_estimadas}
                placeholder={t('ot.admision.opcional')} onChange={e => set('horas_estimadas', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.grupo')} *</label>
              <select className={inp('grupo')} value={plan.grupo_id} onChange={e => elegirGrupo(e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}{ot.grupo_propuesto?.id === g.id ? ` · ${t('ot.admision.propuesto')}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.responsable')} *</label>
              <select className={inp('responsable')} value={plan.responsable_id} disabled={!plan.grupo_id} onChange={e => set('responsable_id', e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {miembrosDe(grupos, plan.grupo_id).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
{!plan.grupo_id && <p className="text-[11px] text-gray-400 mt-1">{t('ot.planificar.primeroGrupo')}</p>}
              {plan.grupo_id && !miembrosDe(grupos, plan.grupo_id).length && <p className="text-[11px] text-amber-700 mt-1">{t('ot.planificar.grupoSinPersonal')}</p>}
            </div>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            <p className="font-medium text-gray-800 mb-1">{t('ot.admision.tareasQueSeCrean')}</p>
            <ol className="list-decimal list-inside space-y-0.5">
              {sinReceta && <li>{t('ot.transacciones.crear_bom')}</li>}
              <li>{t('ot.transacciones.crear_liberar_of')}{sinReceta && <span className="text-gray-400"> — {t('ot.admision.esperaBom')}</span>}</li>
            </ol>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        {devolviendo ? (
          <div className="mt-4 space-y-2">
            <label className={lbl}>{t('ot.admision.motivo')} *</label>
            <textarea rows={2} autoFocus className="input" value={motivo} onChange={e => setMotivo(e.target.value)}
              placeholder={t('ot.admision.motivoPlaceholder')} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setDevolviendo(false); setError('') }}
                className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">{t('common:actions.cancel')}</button>
              <button type="button" disabled={ocupado} onClick={devolver}
                className="bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-40">{t('ot.acciones.devolver')}</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {acciones.includes('devolver') && (
              <button type="button" onClick={() => setDevolviendo(true)}
                className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">{t('ot.acciones.devolver')}</button>
            )}
            {acciones.includes('aprobar_y_planificar') ? (
              <button type="button" disabled={ocupado} onClick={aprobar}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
                {ocupado ? t('common:actions.saving') : t('ot.acciones.aprobar_y_planificar')}
              </button>
            ) : <p className="text-xs text-gray-500 self-center">{t('ot.admision.sinFuncionEsta')}</p>}
          </div>
        )}
      </div>
    </div>
  )
}

function Dato({ label, ancho, children }) {
  return (
    <div className={ancho ? 'col-span-2 sm:col-span-4' : ''}>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 mt-0.5 whitespace-pre-wrap break-words">{children}</dd>
    </div>
  )
}
